import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  startCitationReport as startCitationReportPoll,
  createCiterightReport,
  CiterightDeferred,
  pollCiterightSession,
  updateQueryInCiterightSession,
  addClaimToCiterightSession,
  searchClaimInCiterightSession,
} from './pollCiterightSession';
import { CitationReport, CitationReportFromApi } from '../types';
import { createReportFromUpload } from '../api';

interface UseCitationReportsOptions {
  onError?: (error: string) => void;
  highlight?: (highlights: { text: string; id: string }[]) => void;
  sessionId?: string | null;
  reportId?: number | null;
  publicToken?: string | null;
}

export interface CitationHookResult {
  report: CitationReport | null;
  error: string | null;
  clearError: () => void;
  clearReportError: () => void;
  startCitationReport: (input?: string | File, rawFile?: File) => Promise<void>;
  startSelectedTextCitationCheck: (selectedText: string) => Promise<void>;
  clear: () => void;
  isPolling: boolean;
}

export const useCitationReports = (
  options: UseCitationReportsOptions = {},
): CitationHookResult => {
  const defaultOnError = useCallback((error: string) => {
    console.error('Citeright Error:', error);
  }, []);

  const {
    onError = defaultOnError,
    sessionId,
    reportId,
    highlight,
    publicToken,
  } = options;

  const [report, setReport] = useState<CitationReportFromApi | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportErrorDismissed, setReportErrorDismissed] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const currentSessionRef = useRef<CiterightDeferred | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearReportError = useCallback(() => {
    setReportErrorDismissed(true);
  }, []);

  const replaceDeferred = useCallback(
    (newDeferred: CiterightDeferred) => {
      // Stop existing session if one exists
      if (currentSessionRef.current) {
        currentSessionRef.current.stop();
      }

      // Store the new deferred
      currentSessionRef.current = newDeferred;

      // Mark as polling active
      setIsPolling(true);

      // Set up update callback
      const unsubscribe = newDeferred.onUpdate(
        (updatedReport: CitationReportFromApi) => {
          setReport(updatedReport);
        },
      );

      // Handle promise resolution
      newDeferred.promise
        .then((finalReport: CitationReportFromApi) => {
          setReport(finalReport);
          setError(null); // Clear any previous errors on success
          setReportErrorDismissed(false); // Reset dismissal state on new report
          setIsPolling(false); // Mark polling as complete
        })
        .catch((error: Error) => {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to complete citation check';
          setError(errorMessage);
          onError(errorMessage);
          setIsPolling(false); // Mark polling as stopped on error
        });

      return () => {
        unsubscribe();
        if (currentSessionRef.current === newDeferred) {
          currentSessionRef.current = null;
          setIsPolling(false);
        }
      };
    },
    [onError],
  );

  const reportIdRef = useRef<number | null>(null);
  reportIdRef.current = report?.report_id ?? null;

  const publicTokenRef = useRef<string | null | undefined>(null);
  publicTokenRef.current = publicToken;

  const updateQueryForClaim = useCallback(
    (claimId: string, newQuery: string) => {
      const reportId = reportIdRef.current;

      if (!reportId) {
        throw new Error('Report ID is required');
      }
      const deferred = updateQueryInCiterightSession(
        reportId,
        claimId,
        newQuery,
        publicTokenRef.current ?? undefined,
      );
      replaceDeferred(deferred);
      return deferred;
    },
    [reportIdRef, replaceDeferred],
  );

  const searchClaimById = useCallback(
    (claimId: string) => {
      const reportId = reportIdRef.current;

      if (!reportId) {
        throw new Error('Report ID is required');
      }
      const deferred = searchClaimInCiterightSession(
        reportId,
        claimId,
        publicTokenRef.current ?? undefined,
      );
      replaceDeferred(deferred);
      return deferred;
    },
    [reportIdRef, replaceDeferred],
  );

  const addActionsToReport = useCallback(
    (report: CitationReportFromApi): CitationReport => {
      return {
        ...report,
        // Hide report error if user has dismissed it
        error: reportErrorDismissed ? undefined : report.error,
        claims: report.claims.map((claim) => ({
          ...claim,
          actions: {
            update_query: (newQuery: string) => {
              return updateQueryForClaim(claim.id, newQuery);
            },
            search: () => {
              return searchClaimById(claim.id);
            },
          },
        })),
      };
    },
    [updateQueryForClaim, searchClaimById, reportErrorDismissed],
  );

  useEffect(() => {
    if (publicToken) {
      const deferred = pollCiterightSession({ publicToken });
      replaceDeferred(deferred);
    } else if (sessionId) {
      const deferred = pollCiterightSession({ sessionId });
      replaceDeferred(deferred);
    } else if (reportId) {
      const deferred = pollCiterightSession({ reportId });
      replaceDeferred(deferred);
    }
  }, [sessionId, reportId, publicToken, replaceDeferred]);

  const startCitationReport = useCallback(
    async (input?: string | File, rawFile?: File) => {
      if (!input && !rawFile && reportId) {
        replaceDeferred(startCitationReportPoll(reportId));
        return;
      } else if (!input) {
        throw new Error('Input or Report ID is required');
      }
      try {
        setError(null); // Clear any previous errors
        setReportErrorDismissed(false); // Reset dismissal state for new report
        if (input instanceof File) {
          const response = await createReportFromUpload(input);
          if (!response.report.report_id) {
            setError('Failed to create report from upload');
            return;
          }
          replaceDeferred(startCitationReportPoll(response.report.report_id));
        } else {
          replaceDeferred(createCiterightReport(input, rawFile));
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to start citation report';
        setError(errorMessage);
        onError(errorMessage);
      }
    },
    [replaceDeferred, onError, reportId],
  );

  const startSelectedTextCitationCheck = useCallback(
    async (selectedText: string) => {
      try {
        setError(null); // Clear any previous errors
        setReportErrorDismissed(false); // Reset dismissal state for new check
        const report_id = reportIdRef.current;
        if (!report_id) {
          throw new Error('Report ID is required');
        }
        const deferred = addClaimToCiterightSession(
          report_id,
          selectedText,
          publicTokenRef.current ?? undefined,
        );
        replaceDeferred(deferred);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to start selected text citation check';
        setError(errorMessage);
        onError(errorMessage);
      }
    },
    [reportIdRef, replaceDeferred, onError],
  );

  const clear = useCallback(() => {
    if (currentSessionRef.current) {
      currentSessionRef.current.stop();
      currentSessionRef.current = null;
    }
    setReport(null);
    setIsPolling(false);
  }, []);

  useEffect(() => {
    return () => {
      if (currentSessionRef.current) {
        currentSessionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (highlight) {
      highlight(
        report?.claims.map((claim) => ({
          text: claim.text,
          id: claim.id,
        })) || [],
      );
    }
  }, [report, highlight]);

  const reportWithActions = useMemo(() => {
    return report ? addActionsToReport(report) : null;
  }, [report, addActionsToReport]);

  return {
    report: reportWithActions,
    error,
    clearError,
    clearReportError,
    startCitationReport,
    startSelectedTextCitationCheck,
    clear,
    isPolling,
  };
};
